import { useState, useEffect } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	FormGroup,
	Modal,
	TextField,
	Typography,
} from "@mui/material";
import axios from "axios";
import instance from "../../../apiServices/instance";

const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 650,
	maxHeight: "80vh",
	bgcolor: "background.paper",
	borderRadius: "1rem",
	p: 4,
	overflowY: "auto",
	paddingRight: "0.5rem",
	scrollbarWidth: "thin",
	scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
};

// For Webkit-based browsers (Chrome, Edge, Safari)
const scrollableContentStyle = {
	maxHeight: "60vh",
	overflowY: "auto",
	paddingRight: "0.5rem",
	// borderRadius: "1rem",
	scrollbarWidth: "thin",
	scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
	"&::-webkit-scrollbar": {
		width: "6px",
		borderRadius: "1rem", // Match border radius
	},
	"&::-webkit-scrollbar-track": {
		background: "transparent",
		borderRadius: "1rem",
	},
	"&::-webkit-scrollbar-thumb": {
		background: "rgba(0, 0, 0, 0.2)",
		borderRadius: "1rem",
	},
};

const COMPANY_HOUSE_API_KEY = process.env.REACT_APP_COMPANIES_HOUSE_API_KEY;
const COMPANY_HOUSE_API_URL = process.env.REACT_APP_COMPANY_HOUSE_API_URL;

const OnboardingCompanyModal = ({
	showModal,
	closeModal,
	selectedCompany,
	closeAddCompanyModal,
	fetchCompanies,
}) => {
	const { company_name, company_number, registered_office_address } =
		selectedCompany;

	const [directors, setDirectors] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedDirectors, setSelectedDirectors] = useState({});
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");

	const fetchDirectors = async (query) => {
		setLoading(true);
		try {
			const response = await axios.get(
				`${COMPANY_HOUSE_API_URL}/company/${company_number}/officers`,
				{
					headers: {
						Authorization: COMPANY_HOUSE_API_KEY,
					},
				}
			);
			setDirectors(response.data.items);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchDirectors();
	}, []);

	const handleCheckboxChange = (directorName) => {
		setSelectedDirectors((prev) => {
			const newState = { ...prev };
			if (newState[directorName] !== undefined) {
				delete newState[directorName];
			} else {
				newState[directorName] = "";
			}
			return newState;
		});
	};

	const handleEmailChange = (directorName, email) => {
		setSelectedDirectors((prev) => ({
			...prev,
			[directorName]: email,
		}));
	};

	const handleSubmit = async () => {
		const formattedData = Object.entries(selectedDirectors).map(
			([name, email]) => ({
				name,
				email,
				mainContact: true,
			})
		);

		const address = `${registered_office_address.address_line_1} ${registered_office_address.locality} ${registered_office_address.postal_code}`;
		if (firstName && lastName && email) {
			formattedData.push({
				name: `${firstName} ${lastName}`,
				email,
				mainContact: true,
			});
		}
		try {
			const res = await instance.post("/api/company-info/add-company", {
				companyName: company_name,
				companiesHouseNumber: company_number,
				teamDetails: formattedData,
				registeredAddress: address,
				status: "Onboarding Email Sent",
				selectedCompany,
				directors,
			});
			if (res.data.success) {
				await fetchCompanies();
				closeAddCompanyModal();
			}
		} catch (error) {
			console.log("Error in onboarding client manually: ", error);
		}
	};

	return (
		<Modal
			open={showModal}
			onClose={closeModal}
			aria-labelledby='modal-modal-title'
			aria-describedby='modal-modal-description'>
			<Box sx={style}>
				<Box sx={scrollableContentStyle}>
					{loading ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								margin: "1rem 0",
								alignItems: "center",
							}}>
							<CircularProgress size={24} />
						</Box>
					) : (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: "1rem",
							}}>
							<Typography
								textAlign='center'
								variant='h6'
								fontWeight='bold'>
								{company_name}
							</Typography>

							<Typography variant='h6' fontWeight='bold'>
								Onboarding Contact
							</Typography>

							<Typography variant='body1'>Directors</Typography>

							<FormGroup>
								{directors.map((director) => (
									<Box
										key={director.name}
										sx={{
											display: "flex",
											alignItems: "center",
											border: "1px solid #ccc",
											padding: "1rem",
											marginBottom: "1rem",
											borderRadius: "5px",
											gap: "1rem",
										}}>
										<FormControlLabel
											sx={{ width: "30%" }}
											control={
												<Checkbox
													checked={
														selectedDirectors[
															director.name
														] !== undefined
													}
													onChange={() =>
														handleCheckboxChange(
															director.name,
															director.date_of_birth,
															director.address,
															director.person_number
														)
													}
													sx={{
														color: "#800080",
														"&.Mui-checked": {
															color: "#800080",
														},
													}}
												/>
											}
											label={director.name}
										/>
										{selectedDirectors[director.name] !==
											undefined && (
											<TextField
												sx={{
													width: "80%",
													"& .MuiOutlinedInput-root":
														{
															borderRadius: 0,
															borderBottom:
																"2px solid #ccc",
															"& fieldset": {
																border: "none",
															},
															"&:hover": {
																borderBottom:
																	"2px solid #aaa",
															},
															"&.Mui-focused": {
																borderBottom:
																	"2px solid #800080",
															},
														},
													"& .MuiOutlinedInput-input":
														{
															padding: "8px",
														},
												}}
												variant='outlined'
												value={
													selectedDirectors[
														director.name
													] || ""
												}
												onChange={(e) =>
													handleEmailChange(
														director.name,
														e.target.value
													)
												}
											/>
										)}
									</Box>
								))}
							</FormGroup>

							<Typography variant='body1'>
								Add Manually
							</Typography>

							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: "1rem",
								}}>
								<TextField
									variant='outlined'
									label='First Name'
									fullWidth
									value={firstName}
									onChange={(e) =>
										setFirstName(e.target.value)
									}
								/>
								<TextField
									variant='outlined'
									label='Last Name'
									fullWidth
									value={lastName}
									onChange={(e) =>
										setLastName(e.target.value)
									}
								/>
								<TextField
									variant='outlined'
									label='Email'
									fullWidth
									value={email}
									onChange={(e) => setEmail(e.target.value)}
								/>
							</Box>

							<Box
								sx={{
									display: "flex",
									justifyContent: "flex-start",
									marginTop: "2rem",
								}}>
								<Button
									variant='contained'
									sx={{
										backgroundColor: "#800080",
										color: "#fff",
									}}
									onClick={handleSubmit}>
									Create Client & Send Onboarding Email
								</Button>
							</Box>
						</Box>
					)}
				</Box>
			</Box>
		</Modal>
	);
};

export default OnboardingCompanyModal;
